import { i18n } from 'i18n';

/**
 * Localisation for the embedded Form.io builder and renderer.
 *
 * Form.io looks translations up either by the English source text (builder chrome, palette titles,
 * settings dialog labels) or by a short key (validation and runtime messages). Both sets are served
 * from the lingui catalogues so the strings live in src/Localization with everything else: the
 * catalogue msgid is Form.io's English text, and the value handed to Form.io is the lingui lookup.
 * Anything not listed here falls back to Form.io's built-in English.
 */

// English source text used verbatim as the Form.io key. Each entry is an explicit i18n._() call so
// `lingui extract` registers the msgid.
const uiStrings = (): [string, string][] => [
  // Builder chrome
  ['Drag and Drop a form component', i18n._('Drag and Drop a form component')],
  ['Search field(s)', i18n._('Search field(s)')],
  ['Preview', i18n._('Preview')],
  ['Save', i18n._('Save')],
  ['Cancel', i18n._('Cancel')],
  ['Remove', i18n._('Remove')],
  ['Help', i18n._('Help')],
  ['Component', i18n._('Component')],
  ['Edit', i18n._('Edit')],
  ['Edit JSON', i18n._('Edit JSON')],
  ['Copy', i18n._('Copy')],
  ['Move', i18n._('Move')],
  ['Paste below', i18n._('Paste below')],
  // Settings dialog tabs
  ['Display', i18n._('Display')],
  ['Data', i18n._('Data')],
  ['Validation', i18n._('Validation')],
  ['API', i18n._('API')],
  ['Conditional', i18n._('Conditional')],
  ['Logic', i18n._('Logic')],
  ['Layout', i18n._('Layout')],
  // Palette component titles
  ['Text Field', i18n._('Text Field')],
  ['Text Area', i18n._('Text Area')],
  ['Number', i18n._('Number')],
  ['Date / Time', i18n._('Date / Time')],
  ['Checkbox', i18n._('Checkbox')],
  ['Select Boxes', i18n._('Select Boxes')],
  ['Radio', i18n._('Radio')],
  ['Select', i18n._('Select')],
  ['Signature', i18n._('Signature')],
  ['Panel', i18n._('Panel')],
  ['Field Set', i18n._('Field Set')],
  ['Columns', i18n._('Columns')],
  ['Table', i18n._('Table')],
  ['Tabs', i18n._('Tabs')],
  ['Content', i18n._('Content')],
  // Common settings labels
  ['Label', i18n._('Label')],
  ['Label Position', i18n._('Label Position')],
  ['Placeholder', i18n._('Placeholder')],
  ['Description', i18n._('Description')],
  ['Tooltip', i18n._('Tooltip')],
  ['Prefix', i18n._('Prefix')],
  ['Suffix', i18n._('Suffix')],
  ['Required', i18n._('Required')],
  ['Hidden', i18n._('Hidden')],
  ['Disabled', i18n._('Disabled')],
  ['Default Value', i18n._('Default Value')],
  ['Multiple Values', i18n._('Multiple Values')],
  ['Values', i18n._('Values')],
  ['Value', i18n._('Value')],
  ['Title', i18n._('Title')],
  ['Collapsible', i18n._('Collapsible')],
  ['Theme', i18n._('Theme')],
  ['Custom CSS Class', i18n._('Custom CSS Class')],
  ['Property Name', i18n._('Property Name')],
  ['Width', i18n._('Width')],
  ['Size', i18n._('Size')],
  ['Type', i18n._('Type')],
  ['Name', i18n._('Name')],
  ['Minimum Length', i18n._('Minimum Length')],
  ['Maximum Length', i18n._('Maximum Length')],
  ['Minimum Value', i18n._('Minimum Value')],
  ['Maximum Value', i18n._('Maximum Value')],
  ['Number of Rows', i18n._('Number of Rows')],
  ['Number of Columns', i18n._('Number of Columns')],
  ['Custom Error Message', i18n._('Custom Error Message')],
  ['Error Label', i18n._('Error Label')],
  ['Advanced', i18n._('Advanced')],
  ['Simple', i18n._('Simple')],
  ['Add Another', i18n._('Add Another')],
  ['Yes', i18n._('Yes')],
  ['No', i18n._('No')],
];

// Form.io short keys for runtime/validation messages. `{{field}}` etc. are Form.io placeholders and
// are kept outside the translated fragment.
const messageStrings = (): [string, string][] => [
  ['required', `{{field}} ${i18n._('is required')}`],
  ['minLength', `{{field}} ${i18n._('must have at least')} {{length}} ${i18n._('characters')}.`],
  ['maxLength', `{{field}} ${i18n._('must have no more than')} {{length}} ${i18n._('characters')}.`],
  ['min', `{{field}} ${i18n._('cannot be less than')} {{min}}.`],
  ['max', `{{field}} ${i18n._('cannot be greater than')} {{max}}.`],
  ['invalid_email', `{{field}} ${i18n._('must be a valid email.')}`],
  ['invalid_date', `{{field}} ${i18n._('is not a valid date.')}`],
  ['error', i18n._('Please fix the following errors before submitting.')],
  ['submitError', i18n._('Please check the form and correct all errors before submitting.')],
  ['complete', i18n._('Submission Complete')],
  ['cancel', i18n._('Cancel')],
  ['submit', i18n._('Submit Form')],
  ['next', i18n._('Next')],
  ['previous', i18n._('Previous')],
  ['yes', i18n._('Yes')],
  ['no', i18n._('No')],
  ['addAnother', i18n._('Add Another')],
  ['noResultsFound', i18n._('No results found')],
  ['typeToSearch', i18n._('Type to search')],
  ['clickToSign', i18n._('Click to Sign')],
  ['loading', i18n._('Loading')],
  ['confirmComponentRemove', i18n._('Are you sure you want to remove this component?')],
  ['confirmComponentRemoveWithChildren', i18n._('Removing this component will also remove all of its children. Are you sure you want to do this?')],
  ['yesDelete', i18n._('Yes, delete it')],
  ['wantToClearData', i18n._('Do you want to clear data?')],
  ['copy', i18n._('Copy')],
  ['move', i18n._('Move')],
  ['edit', i18n._('Edit')],
  ['editJson', i18n._('Edit JSON')],
  ['remove', i18n._('Remove')],
  ['pasteBelow', i18n._('Paste below')],
  ['month', i18n._('Month')],
  ['day', i18n._('Day')],
  ['year', i18n._('Year')],
];

/**
 * Options to spread into `Formio.builder(...)` / `Formio.createForm(...)` so the Form.io UI follows
 * the app's active language. English needs nothing: Form.io's defaults already are the msgids.
 */
export const getFormioI18nOptions = (): { language?: string; i18n?: Record<string, Record<string, string>> } => {
  const language = i18n.locale;
  if (!language || language === 'en') return {};

  return {
    language,
    i18n: {
      [language]: Object.fromEntries([...uiStrings(), ...messageStrings()]),
    },
  };
};

export default getFormioI18nOptions;
